import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import './ChartKHPBTT.css';

const ChartKHPBTT = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPhaseOverview();
  }, []);

  const fetchPhaseOverview = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/PhaseOverview`);
      const result = await response.json();
      
      if (result.success) {
        setData(result.data);
      } else {
        setError('Failed to fetch data');
      }
    } catch (err) {
      setError('Error fetching data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{data.name}</p>
          <p className="tooltip-value">
            {`${data.value} phase${data.quantity ? ` (${data.quantity})` : ''}`}
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom label component for center display
  const renderCustomizedLabel = ({ cx, cy }) => {
    return (
      <g>
        <text 
          x={cx} 
          y={cy + 15} 
          textAnchor="middle" 
          dominantBaseline="central"
          className="center-label-text"
        >
          Tổng số đợt: {data.totalPhases}
        </text>
      </g>
    );
  };

  if (loading) return <div className="text-center p-4">Đang tải...</div>;
  if (error) return <div className="text-center p-4 text-red-500">{error}</div>;
  if (!data) return <div className="text-center p-4">Không có dữ liệu</div>;

  const chartData = [
    {
      name: 'Đã hoàn thành',
      value: data.completed,
      color: '#10b981',
      quantity: null
    },
    {
      name: 'Đợt hiện tại',
      value: 1,
      color: '#3b82f6',
      quantity: data.current ? `${data.current.producedQty}/${data.current.planQty} sp` : null
    },
    { 
      name: 'Đợt tiếp theo',
      value: data.next ? 1 : 0,
      color: '#f59e0b',
      quantity: data.next ? `${data.next.planQty} sp` : null
    },
    {
      name: 'Chưa sản xuất',
      value: data.remainingPhases - (data.next ? 1 : 0),
      color: '#9ca3af',
      quantity: `${data.remainingQty} sp`
    }
  ].filter(item => item.value > 0);

  const totalQty = data.remainingQty + (data.current?.producedQty || 0);

  return (
    <div className="chart-container">
      <div className="chart-content p-6">
        <h2 className="chart-title">Tổng quan Phase sản xuất</h2>
        
        {/* Chart */}
        <div className="chart-wrapper">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={120}
                innerRadius={80}
                fill="#8884d8"
                dataKey="value"
                animationBegin={0}
                animationDuration={800}
                stroke="none"
              >
                {chartData.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.color}
                    stroke={entry.color}
                    strokeWidth={3}
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          
          {/* Custom Legend */}
          <div className="custom-legend">
            {chartData.map((entry, index) => (
              <div key={index} className="legend-item">
                <div 
                  className="legend-color" 
                  style={{ backgroundColor: entry.color }}
                ></div>
                <span className="legend-text">{entry.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Statistics */}
        <div className="stats-grid">
          <div className="stat-card completed">
            <div className="stat-value" style={{color: '#10b981'}}>{data.completed}</div>
            <div className="stat-label">Đã hoàn thành</div>
            <div className="stat-quantity">Phases hoàn tất</div>
          </div>
          
          <div className="stat-card current">
            <div className="stat-value" style={{color: '#3b82f6'}}>
              {data.current ? data.current.phaseId : 'N/A'}
            </div>
            <div className="stat-label">Đợt hiện tại</div>
            <div className="stat-quantity">
              {data.current ? `${data.current.producedQty.toLocaleString()}/${data.current.planQty.toLocaleString()} sp` : 'Không có'}
            </div>
          </div>
          
          <div className="stat-card next">
            <div className="stat-value" style={{color: '#f59e0b'}}>
              {data.next ? data.next.phaseId : 'N/A'}
            </div>
            <div className="stat-label">Đợt tiếp theo</div>
            <div className="stat-quantity">
              {data.next ? `${data.next.planQty.toLocaleString()} sản phẩm` : 'Không có'}
            </div>
          </div>
          
          <div className="stat-card remaining">
            <div className="stat-value" style={{color: '#6b7280'}}>
              {data.remainingPhases - (data.next ? 1 : 0)}
            </div>
            <div className="stat-label">Chưa sản xuất</div>
            <div className="stat-quantity">{data.remainingQty.toLocaleString()} sản phẩm</div>
          </div>

          <div className="stat-card" style={{'--accent-color': '#8b5cf6'}}>
            <div className="stat-value" style={{color: '#8b5cf6'}}>
              {totalQty?.toLocaleString() || 'N/A'}
            </div>
            <div className="stat-label">Tổng sản lượng</div>
            <div className="stat-quantity">Tất cả các đợt</div>
          </div>
        </div>

        {/* Current Phase Details */}
        {data.current && (
          <div className="phase-detail">
            <h3 className="phase-title">{data.current.name}</h3>
            <div className="phase-info-grid">
              <div className="phase-info-item">
                <div className="phase-info-label">Bắt đầu</div>
                <div className="phase-info-value">
                  {new Date(data.current.startDate).toLocaleDateString('vi-VN')}
                </div>
              </div>
              <div className="phase-info-item">
                <div className="phase-info-label">Kết thúc</div>
                <div className="phase-info-value">
                  {new Date(data.current.endDate).toLocaleDateString('vi-VN')}
                </div>
              </div>
              <div className="phase-info-item">
                <div className="phase-info-label">Sản lượng</div>
                <div className="phase-info-value">
                  {data.current.producedQty.toLocaleString()}/{data.current.planQty.toLocaleString()}
                </div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{width: `${data.current.progress}%`}}
                  ></div>
                </div>
              </div>
              <div className="phase-info-item">
                <div className="phase-info-label">Tiến độ</div>
                <div className="phase-info-value">{data.current.progress}%</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChartKHPBTT;
